import React, { useState, useRef, useMemo, useCallback, useEffect } from "react";
import { TextField, SortMenu, ButtonMenu, Button } from "@ann/web-components";
import {
  FiltersContainer,
  FiltersLeftContent,
  FiltersRightContent,
  SearchContainer,
} from "./Filters.styled";

const Filters = ({
  onSearchChange,
  onStatusChange,
  onSortChange,
  onOrderChange,
  selectedSortOption = "name",
  orderDirection = "asc",
  loading = false,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [filtersKey, setFiltersKey] = useState(0);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const searchTimeoutRef = useRef(null);

  const handleSearchChange = useCallback((value) => {
    setSearchValue(value);
    
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Set new timeout for 500ms
    searchTimeoutRef.current = setTimeout(() => {
      onSearchChange?.(value);
    }, 500);
  }, [onSearchChange]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleStatusChange = useCallback((value) => {
    // ButtonMenu onChange receives the value of selected item
    // If same value is selected, clear the filter (toggle off)
    const newStatus = selectedStatus === value ? null : value;
    setSelectedStatus(newStatus);
    onStatusChange?.(newStatus);
  }, [onStatusChange, selectedStatus]);

  const handleClearFilters = useCallback(() => {
    setSelectedStatus(null);
    setSearchValue("");
    setFiltersKey((prev) => prev + 1);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    onStatusChange?.(null);
    onSearchChange?.("");
  }, [onStatusChange, onSearchChange]);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return selectedStatus !== null || searchValue.trim() !== "";
  }, [selectedStatus, searchValue]);

  // Status options for filter
  const statusOptions = [
    { label: "Processing", value: "processing" },
    { label: "Done", value: "done" },
  ];

  // Get label for status filter button
  const getStatusLabel = () => {
    const option = statusOptions.find(opt => opt.value === selectedStatus);
    return option ? option.label : "Status";
  };

  return (
    <FiltersContainer>
      <FiltersLeftContent>
        <SearchContainer>
          <TextField
            iconLeft="search"
            onChange={handleSearchChange}
            size="small"
            placeholder="Search"
            value={searchValue}
            disabled={loading}
          />
        </SearchContainer>

        <ButtonMenu
          key={`status-filter-${filtersKey}`}
          label={getStatusLabel()}
          menuPosition="bottom"
          onChange={handleStatusChange}
          size="medium"
          type="secondary"
          width="auto"
          disabled={loading}
        >
          <ButtonMenu.Menu>
            {statusOptions.map((option) => (
              <ButtonMenu.Item
                key={option.value}
                label={option.label}
                value={option.value}
              >
                {option.label}
              </ButtonMenu.Item>
            ))}
          </ButtonMenu.Menu>
        </ButtonMenu>

        {hasActiveFilters && (
          <Button
            label="Clear Filters"
            size="medium"
            type="secondary"
            onClick={handleClearFilters}
            disabled={loading}
          />
        )}
      </FiltersLeftContent>
      <FiltersRightContent>
        <SortMenu
          minWidth="200px"
          onChange={(id) => {
            if (onSortChange) {
              onSortChange(id);
            }
          }}
          order={(orderDirection || "asc").toLowerCase()}
          onOrderChange={() => {
            if (onOrderChange) {
              // Toggle order in parent component
              onOrderChange();
            }
          }}
          options={[
            {
              icon: 'sortByAlpha',
              id: 'name',
              label: 'Name'
            },
            {
              icon: 'clock',
              id: 'uploaded',
              label: 'Uploaded'
            },
            {
              icon: 'trendingUp',
              id: 'progress',
              label: 'Progress'
            }
          ]}
          selected={selectedSortOption || "name"}
          size="medium"
          title="SORT BY"
          width="200px"
          disabled={loading}
        />
      </FiltersRightContent>
    </FiltersContainer>
  );
};

export default Filters;
